import { useAtom } from 'jotai';
import React, { useMemo, useState } from 'react';
import { AdminSettingsSection, Checkbox, Input } from 'src/components';
import ReactSwitch from 'react-switch';
import { ACTION_TYPES } from 'src/shared/settings/base';
import { imageSettingsAtom } from 'src/shared/settings/image';

import styles from '../styles.module.scss';

type TCustomImageConversions = {};

export const CustomImageConversions: React.FC<TCustomImageConversions> = () => {
  const [imageSettings, updateImageSettings] = useAtom(imageSettingsAtom);
  const [errors, setErrors] = useState<any>(null);

  const conversions = useMemo(
    () => imageSettings.conversions.filter((c: any) => c.key !== 'deepzoom'),
    [imageSettings.conversions],
  );

  const handleChanges = (id: number, property: string, value: string | boolean) => {
    updateImageSettings({
      type: ACTION_TYPES.UPDATE_STATE,
      payload: {
        conversions: {
          id,
          value: {
            [property]: value,
          },
        },
      },
    });
  };

  const handleKeyChange = (id: number, value: string) => {
    const image = conversions.find((img: any) => img.key === value);
    if (image) {
      setErrors({
        [id]: {
          ...(errors && errors[id]),
          key: `${value} already exists`,
        },
      });
      return;
    }

    updateImageSettings({
      type: ACTION_TYPES.UPDATE_STATE,
      payload: {
        conversions: {
          id,
          key: value,
        },
      },
    });
  };

  const validateSize = (id: number, value: string) => {
    if (!value) return;
    if (!value.match(/^\d+x\d+$/g)) {
      setErrors({
        [id]: {
          ...(errors && errors[id]),
          size: 'Size must be in format "WIDTHxHEIGHT"',
        },
      });
      return;
    }
  };

  const handleSizeChange = (id: number, value: string) => {
    setErrors((state: any) => {
      if (!state) return state;

      delete state[id];

      return state;
    });

    updateImageSettings({
      type: ACTION_TYPES.UPDATE_STATE,
      payload: {
        conversions: {
          id,
          size: value,
        },
      },
    });
  };

  if (conversions.length === 0) {
    return null;
  }

  return (
    <AdminSettingsSection title="Custom Image Conversions">
      <p>Enable the conversion to create it when an image is uploaded</p>
      <div className={styles.imageConvCardGrid}>
        {conversions.map(({ key, value, size, id }: any) => (
          <div key={id} className={styles.imageConvCard}>
            <div className={styles.imageConvCardHeader}>
              <span>Create on upload</span>
              <ReactSwitch
                checked={value.enabled}
                onChange={(checked: boolean) => handleChanges(id, 'enabled', checked)}
                checkedIcon={false}
                uncheckedIcon={false}
                onColor="#2e53e3"
                width={40}
                height={20}
                handleDiameter={16}
              />
            </div>

            <div className={styles.imageConvCardRow}>
              <div className={styles.imageConvCardField}>
                <label>Key</label>
                <Input variant="secondary" value={key} onChange={(val) => handleKeyChange(id, val)} />
                {errors && errors[id]?.key && (
                  <span className={styles.imageConvFieldError}>{errors[id].key}</span>
                )}
              </div>
              <div className={styles.imageConvCardField}>
                <label>Size (WxH)</label>
                <Input
                  variant="secondary"
                  value={size}
                  onChange={(val) => handleSizeChange(id, val)}
                  onBlur={() => validateSize(id, size)}
                />
                {errors && errors[id]?.size && (
                  <span className={styles.imageConvFieldError}>{errors[id].size}</span>
                )}
              </div>
            </div>

            <div className={styles.imageConvCardBody}>
              <div className={styles.imageConvCardField}>
                <label>Watermark</label>
                <Checkbox
                  checked={value.watermark}
                  onChange={(e) => handleChanges(id, 'watermark', e.target.checked)}
                />
              </div>
              <div className={styles.imageConvCardField}>
                <label>Public</label>
                <Checkbox
                  checked={value.public}
                  onChange={(e) => handleChanges(id, 'public', e.target.checked)}
                />
              </div>
            </div>
          </div>
        ))}
      </div>
    </AdminSettingsSection>
  );
};
